// API Service for backend communication

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5202/api';
const BACKEND_BASE_URL = API_BASE_URL.replace(/\/api$/, '');

/**
 * Constructs a full URL for static assets (images, files) from the backend.
 * Handles relative paths like "/uploads/..." by prefixing the backend URL.
 */
export const getAssetUrl = (path: string | undefined | null): string => {
  if (!path) return '';
  // If already a full URL, return as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${BACKEND_BASE_URL}${normalizedPath}`;
};

export interface ApiErrorData {
  error?: string;
  errors?: string[];
  message?: string;
}

// Auth event listeners for global auth state management
type AuthEventListener = () => void;
const authEventListeners: AuthEventListener[] = [];

export const onAuthError = (listener: AuthEventListener) => {
  authEventListeners.push(listener);
  return () => {
    const index = authEventListeners.indexOf(listener);
    if (index > -1) {
      authEventListeners.splice(index, 1);
    }
  };
};

const notifyAuthError = () => {
  authEventListeners.forEach(listener => listener());
};

class ApiService {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('accessToken');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    skipAuthErrorNotification = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = {
      ...this.getAuthHeaders(),
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData: ApiErrorData = await response.json().catch(() => ({}));

      // Notify listeners on 401 Unauthorized (unless explicitly skipped)
      if (response.status === 401 && !skipAuthErrorNotification) {
        notifyAuthError();
      }

      throw new ApiError(response.status, errorData);
    }

    // Handle empty responses
    const text = await response.text();
    if (!text) return {} as T;

    return JSON.parse(text);
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export class ApiError extends Error {
  status: number;
  data: ApiErrorData;

  constructor(status: number, data: ApiErrorData) {
    const message = data.error || data.errors?.join(', ') || data.message || 'An error occurred';
    super(message);
    this.status = status;
    this.data = data;
    this.name = 'ApiError';
  }
}

export const api = new ApiService(API_BASE_URL);
